/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from "react";
import { providers } from "dc-core";
import TextManager from "dc-preview-utils/src/pageContentHandler/TextManager";
import { HyperLinkManager } from "./HyperLinkManager";
import { transformViewToPDF } from "dc-preview-utils/src/Utils/PageUtils";
import { isTextSelectionEnabled, isPanActive, getShouldEnableOutline, getCurrRHPFeature } from "selectors/appSelectors";
import { getCoordinatesRelativeToPage } from "utils/EventHandlingUtils";
import {
  mouseButtons,
  findPressedMouseButton,
  getTouchCoordinates,
  isControlORCommandPressed
} from "dc-preview-utils/src/Utils/EventHandlingUtils";
import { getSearchManager } from "selectors/searchSelectors";
import { getPageCoordinates, getTouchPageIndex } from "selectors/pageNumber";
import * as hitTester from "./HitTester";
import { getPageZoomLevel } from "selectors/zoom";
import { getCursorStateChange, getEnableImageSelection, getIsDemoMode } from "selectors/overlayDropinSelectors";
import { subscribe } from "utils/keyboard";
import * as codes from "dc-preview-utils/src/Constants/key-codes";
import { canvasOperation, CaretShortcut, textManagerMode } from "dc-preview-utils/src/Constants/enums";
import {
  isTouchSelectionPresent,
  isThereAnySelection,
  getTextInSelection
} from "dc-preview-utils/src/selectors/touchSelectors";
import { getCanvasOperation } from "dc-preview-utils/src/selectors/pageHighlightsSelectors";
import PageCanvasCss from "components/document/page/PageCanvas.css";
import PagesViewCss from "components/document/page/PagesView.css";
import { contextMenuManager } from "../contextMenuManager";
import { flatten, isFunction } from "utils/lodashUtils";
import * as stores from "stores";
import { getScrollbarSize } from "selectors/appUiStateSelectors";
import { isDialog, isTextArea, isButton, isSelectElement } from "utils/DomUtils";
import { contextMenuTriggerType } from "dc-preview-utils/src/Constants/enums";
import { printHandler } from "print/printHandler";
import { overlayEvents } from "overlays/OverlayEvents";
import { selectionHandler } from "./selectionHandler";
import CopyTextIcon from "components/common/CopyTextIcon";
import {
  getShouldUnblockUIFeatures,
  getLatestHitTestResponse,
  isFullScreenMode,
  getIsImageCurrentSelection
} from "selectors/documentSelectors";
import {
  cursorType,
  KeyboardNavigationDirection,
  OUTLINE,
  VERB_COPY_TEXT,
  VERB_QANDA,
  VERB_EXPRESS_TEXT_TO_IMAGE_CONTEXT_MENU
} from "constants/enums";
import { getCaretMode } from "selectors/caretModeSelectors";
import { moveCaretToDocBegin, moveCaretToDocEnd } from "utils/CaretModeUtils";
import { getIsCursorVisible } from "dc-preview-utils/src/selectors/cursorSelectors";
import { isContextMenuVisible } from "selectors/contextMenuSelectors";
import { getShouldEnableQnA } from "selectors/appSelectors";
import { isCopyAllowed } from "utils/DocPermissionsUtils";
import { resetCaretMode, isCaretModeEnabledInActiveVerb, shouldHideCaret } from "utils/CaretModeUtils";
import { getIsFocusableAccessibleDOMNode } from "utils/AccessibilityUtils";
import { getFeatureFlagApis } from "publicApis/featureFlag";
import { KEYBOARD_SHORTCUTS } from "constants/constants";
import { throttle } from "utils/lodashUtils";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";

class PageContentHandler {
  constructor() {
    this.reset();
    overlayEvents.on("pagesViewScrolled", evt => {
      this.handleScroll(evt);
    });
    this._throttledPageHoverEvent = throttle(this._pageHoverEvent, 100, { leading: false, trailing: true });
  }

  reset() {
    this._hyperLinkManager = new HyperLinkManager();
    this._textManager = new TextManager({ stores, selectionHandler });
    this._dragInProgress = false;
    this._handleKeyboardEvents = this._handleKeyboardEvents.bind(this);
    this.invokeTextManagerMethod = this.invokeTextManagerMethod.bind(this);
    subscribe(this._handleKeyboardEvents);
    contextMenuManager.subscribe({
      name: "PageContentHandlerRightClick",
      contextMenuHandler: this.onContextMenu.bind(this),
      type: contextMenuTriggerType.RIGHT_CLICK
    });
    contextMenuManager.subscribe({
      name: "PageContentHandlerRightClick",
      contextMenuHandler: this.onContextMenuTextSelectionRightClick.bind(this),
      type: contextMenuTriggerType.RIGHT_CLICK
    });
    contextMenuManager.subscribe({
      name: "PageContentHandlerTextSelection",
      contextMenuHandler: this.onContextMenuTextSelection.bind(this),
      type: contextMenuTriggerType.TEXT_SELECTION
    });
    if (providers && providers["keyboard-shortcuts-provider"]) {
      providers["keyboard-shortcuts-provider"]().then(shortcutManager => {
        if (shortcutManager && shortcutManager.addShortcuts)
          shortcutManager.addShortcuts([
            {
              id: KEYBOARD_SHORTCUTS.CANCEL_IMAGE_SELECTION,
              shortcutExprs: ["Escape"],
              handler: () => {
                const isImageCurrentSelection = getIsImageCurrentSelection(stores);
                if (isImageCurrentSelection) {
                  this.invokeTextManagerMethod("clearSelection", undefined, stores);
                }
                return { shouldPreventDefault: true };
              }
            }
          ]);
      });
    }
  }

  /* This method is a wrapper over textManager. It expects methodName to call in textManager, a defaultValue in case the function is not called if the condition fails and the parameters to pass in function call */
  invokeTextManagerMethod(methodName, defaultValue, ...args) {
    if (isTextSelectionEnabled(stores) && isFunction(this._textManager[methodName]))
      return this._textManager[methodName](...args);
    return defaultValue;
  }

  _shouldCallCaretTextManagerMethod() {
    return (
      getCaretMode(stores) &&
      !isContextMenuVisible(stores) &&
      (getIsCursorVisible(stores) || isThereAnySelection(stores))
    );
  }

  _handleKeyboardEvents(evt) {
    const shouldPreventTextSelectionEvent =
      isTextArea(document.activeElement) ||
      (isButton(document.activeElement) && !getIsFocusableAccessibleDOMNode(document.activeElement)) ||
      isSelectElement(document.activeElement);
    if (isDialog(document.activeElement)) return false;
    let handled, snapToWords;
    switch (evt.keyCode) {
      case codes.C:
        var selection = document.getSelection(); // check for existing text selection
        if (
          selection &&
          selection.anchorNode &&
          selection.anchorNode.nodeType === Node.TEXT_NODE &&
          isCopyAllowed(stores)
        ) {
          return false;
        }
        if (isTextArea(document.activeElement)) return false;
        if (isControlORCommandPressed(evt)) {
          this.invokeTextManagerMethod("copyText", undefined);
          return { shouldPreventDefault: false, handled: true };
        }
        return false;
      case codes.ARROW_RIGHT:
        if (shouldPreventTextSelectionEvent) return false;
        snapToWords = isControlORCommandPressed(evt);
        if (evt.shiftKey) {
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.RIGHT,
            snapToWords ? 1 : 0
          );
          return { shouldPreventDefault: handled, handled };
        }
        if (this._shouldCallCaretTextManagerMethod()) {
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.RIGHT,
            snapToWords ? 1 : 0,
            CaretShortcut.NONE,
            textManagerMode.CARET
          );
          return { shouldPreventDefault: handled, handled };
        }
        return false;

      case codes.ARROW_LEFT:
        if (shouldPreventTextSelectionEvent) return false;
        snapToWords = isControlORCommandPressed(evt);
        if (evt.shiftKey) {
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.LEFT,
            snapToWords ? 1 : 0
          );
          return { shouldPreventDefault: handled, handled };
        }
        if (this._shouldCallCaretTextManagerMethod()) {
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.LEFT,
            snapToWords ? 1 : 0,
            CaretShortcut.NONE,
            textManagerMode.CARET
          );
          return { shouldPreventDefault: handled, handled };
        }
        return false;

      case codes.ARROW_UP:
        if (shouldPreventTextSelectionEvent) return false;
        if (evt.shiftKey) {
          handled = this.invokeTextManagerMethod("keyboardSelection", false, KeyboardNavigationDirection.UP, 0);
          return { shouldPreventDefault: handled, handled };
        }
        if (this._shouldCallCaretTextManagerMethod()) {
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.UP,
            0,
            CaretShortcut.NONE,
            textManagerMode.CARET
          );
          return { shouldPreventDefault: handled, handled };
        }
        return false;

      case codes.ARROW_DOWN:
        if (shouldPreventTextSelectionEvent) return false;
        if (evt.shiftKey) {
          handled = this.invokeTextManagerMethod("keyboardSelection", false, KeyboardNavigationDirection.DOWN, 0);
          return { shouldPreventDefault: handled, handled };
        }
        if (this._shouldCallCaretTextManagerMethod()) {
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.DOWN,
            0,
            CaretShortcut.NONE,
            textManagerMode.CARET
          );
          return { shouldPreventDefault: handled, handled };
        }
        return false;

      case codes.HOME:
        if (shouldPreventTextSelectionEvent) return false;
        if (evt.shiftKey) {
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.NONE,
            0,
            CaretShortcut.LINE_BEGIN
          );
          return { shouldPreventDefault: handled, handled };
        }
        if (this._shouldCallCaretTextManagerMethod()) {
          if (isControlORCommandPressed(evt)) {
            handled = moveCaretToDocBegin(stores);
            return { shouldPreventDefault: handled, handled };
          }
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.NONE,
            0,
            CaretShortcut.LINE_BEGIN,
            textManagerMode.CARET
          );
          return { shouldPreventDefault: handled, handled };
        }
        return false;

      case codes.END:
        if (shouldPreventTextSelectionEvent) return false;
        if (evt.shiftKey) {
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.NONE,
            0,
            CaretShortcut.LINE_END
          );
          return { shouldPreventDefault: handled, handled };
        }
        if (this._shouldCallCaretTextManagerMethod()) {
          if (isControlORCommandPressed(evt)) {
            handled = moveCaretToDocEnd(stores);
            return { shouldPreventDefault: handled, handled };
          }
          handled = this.invokeTextManagerMethod(
            "keyboardSelection",
            false,
            KeyboardNavigationDirection.NONE,
            0,
            CaretShortcut.LINE_END,
            textManagerMode.CARET
          );
          return { shouldPreventDefault: handled, handled };
        }
        return false;

      case codes.P:
        if (isControlORCommandPressed(evt) && getShouldUnblockUIFeatures(stores) && !getIsDemoMode(stores)) {
          printHandler.print({
            triggeredFrom: "keyboard"
          });
          return { shouldPreventDefault: true, handled: true };
        }
        return false;
      default:
        return false;
    }
  }

  _isContentUnderMouseTextLike() {
    const latestContentType = getLatestHitTestResponse(stores).contentType;
    return (
      latestContentType === hitTester.pageContentTypes.TEXT ||
      latestContentType === hitTester.pageContentTypes.HYPERLINK
    );
  }

  _handlePanEvent(cursorType) {
    document.querySelectorAll(`.ReactVirtualized__Grid[role="main"]`).forEach(elem => {
      elem.style.cursor = cursorType;
    });
    document.querySelectorAll(`.${PageCanvasCss.parent}`).forEach(elem => {
      elem.style.cursor = cursorType;
    });
  }

  _performHitTesting(evtObj, pageIndex, stores) {
    const x1 = getCoordinatesRelativeToPage(evtObj).x;
    const y1 = getCoordinatesRelativeToPage(evtObj).y;
    const { x, y } = transformViewToPDF(x1, y1, pageIndex, stores);
    // stop auto scroll
    this.invokeTextManagerMethod("stopAutoScroll", undefined);

    return hitTester
      .getContentTypeUnderMouse(pageIndex, x, y, getPageZoomLevel(stores, pageIndex))
      .then(contentType => {
        const hitTestResult = {
          pageNumber: pageIndex + 1,
          x: x1,
          y: y1,
          contentType
        };

        this._shownDetailsOnHover(evtObj, { x, y }, { x: x1, y: y1 }, contentType);

        if (isPanActive(stores)) {
          this._handlePanEvent(cursorType.GRAB);
          if (contentType === hitTester.pageContentTypes.HYPERLINK) {
            if (!getCursorStateChange(stores)) this._hyperLinkManager.handleHover(evtObj);
            stores.documentStore.setLatestHitTestResponse(hitTestResult);
          }
        } else {
          if (getLatestHitTestResponse(stores).contentType !== contentType) {
            overlayEvents.trigger("contenttypeundermousechanged", {
              ...hitTestResult,
              contentType: hitTester.pageContentTypesReverseMap[contentType]
            });
          }
          stores.documentStore.setLatestHitTestResponse(hitTestResult);
          /*
          In our text selection logic, if the mouseState isn't "down," we skip selecting text.
          code below was changing this mouseState to hover and was preventing text selection
          To address this, we've implemented a check: if the current mouseState is "down," don't transition it to "hover."
          As a result, the hover state can only occur in this sequence: down -> up -> hover.
          Also Calling hover or changing cursor doesn't make sense in mouse down state
          */
          if (this._textManager.mouseState !== "down") {
            if (contentType === hitTester.pageContentTypes.TEXT) {
              if (!getCursorStateChange(stores)) {
                document.querySelectorAll(`.${PageCanvasCss.parent}`).forEach(elem => {
                  elem.style.cursor = cursorType.TEXT;
                });
                this.invokeTextManagerMethod("handleHover", undefined, evtObj);
              }
            } else if (contentType === hitTester.pageContentTypes.IMAGE) {
              if (!getCursorStateChange(stores) && getEnableImageSelection(stores)) {
                document.querySelectorAll(`.${PageCanvasCss.parent}`).forEach(elem => {
                  elem.style.cursor = cursorType.IMAGE;
                });
              }
              this.invokeTextManagerMethod("handleHover", undefined, evtObj);
            } else if (contentType === hitTester.pageContentTypes.HYPERLINK) {
              if (!getCursorStateChange(stores)) this._hyperLinkManager.handleHover(evtObj);
            } else if (!getCursorStateChange(stores)) {
              document.querySelectorAll(`.${PageCanvasCss.parent}`).forEach(elem => {
                elem.style.cursor = cursorType.DEFAULT;
              });
            }
          }
        }
        return contentType;
      });
  }

  _isDrag(evt) {
    return findPressedMouseButton(evt) === mouseButtons.LEFT;
  }

  _shownDetailsOnHover(evtObj, pdfPoint, viewPoint) {
    const showDetailsOnHover = getQueryParameterFromUrl(window.location.href, "showDetailsOnHover") === "true";
    if (!showDetailsOnHover) {
      return;
    }

    let hoverDiv = document.getElementById("hover-details-div");
    if (hoverDiv) {
      hoverDiv.remove();
    }

    hoverDiv = document.createElement("div");
    hoverDiv.id = "hover-details-div";
    hoverDiv.style.position = "absolute";
    hoverDiv.style.zIndex = 9999;
    hoverDiv.style.padding = "5px";
    hoverDiv.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
    hoverDiv.style.color = "white";
    hoverDiv.style.borderRadius = "5px";
    hoverDiv.style.pointerEvents = "none";
    hoverDiv.style.fontSize = "12px";

    hoverDiv.innerHTML = `
      <div>
        PDF: (${pdfPoint.x.toFixed(2)}, ${pdfPoint.y.toFixed(2)})<br>
        View: (${viewPoint.x}, ${viewPoint.y})
      </div>
    `;

    hoverDiv.style.left = `${evtObj.clientX + 10}px`;
    hoverDiv.style.top = `${evtObj.clientY + 15}px`;

    document.body.appendChild(hoverDiv);

    setTimeout(() => {
      hoverDiv.remove();
    }, 2000);
  }

  _pageHoverEvent(evtObj, pageIndex, stores) {
    const { x, y } = getCoordinatesRelativeToPage(evtObj);
    const pdfPoint = transformViewToPDF(x, y, pageIndex, stores);
    overlayEvents.trigger("pagehover", {
      eventObj: evtObj,
      params: {
        pageIndex,
        pdfPoint
      }
    });
  }

  handleMouseMove(evtObj, pageIndex, stores) {
    if (!this._isDrag(evtObj) && !isPanActive(stores) && !isTouchSelectionPresent(stores)) {
      this._throttledPageHoverEvent(evtObj, pageIndex, stores);
    }

    const { x, y } = getCoordinatesRelativeToPage(evtObj);
    if (this._isDrag(evtObj)) {
      if (isPanActive(stores)) {
        this._handlePanEvent(cursorType.GRABBING);
      }
      // Don't perform any hit testing when dragging.
      if (this._dragInProgress === false) {
        if (getCanvasOperation(stores) === canvasOperation.CANVAS_OP_FIND) {
          let searchManager = getSearchManager(stores);
          if (searchManager) {
            searchManager.clearSearch();
          }
        }
        stores.pageHighlightsStore.setCanvasOperation(canvasOperation.CANVAS_OP_SELECTION);
      }
      this._dragInProgress = true;
      if (
        this._isContentUnderMouseTextLike() ||
        getLatestHitTestResponse(stores).contentType !== hitTester.pageContentTypes.IMAGE
      ) {
        // treat content as text irrespective when selecting.
        const hitTestResult = {
          pageNumber: pageIndex + 1,
          x,
          y,
          contentType: hitTester.pageContentTypes.TEXT
        };
        stores.documentStore.setLatestHitTestResponse(hitTestResult);
        // prevent default if we handling the drag event.
        evtObj.cancelable !== false && evtObj.preventDefault();
        return this.invokeTextManagerMethod("handleDrag", Promise.resolve(), x, y, evtObj, pageIndex);
      }

      return Promise.resolve();
    }

    return this._performHitTesting(evtObj, pageIndex, stores);
  }

  _isClickOnScrollBar(evt, stores) {
    /**
     * Check only if the target is either PagesView (non reactv) or
     * Grid (reactv)
     */
    const { target, pageX, pageY } = evt;
    if (target.classList.contains(PagesViewCss.PagesView) || target.classList.contains("ReactVirtualized__Grid")) {
      const { bottom, right } = target.getBoundingClientRect();
      const scrollBarSize = getScrollbarSize(stores);

      // vertical scrollbar
      if (target.offsetWidth > target.clientWidth) {
        if (pageX - document.documentElement.scrollLeft > right - scrollBarSize) {
          return true;
        }
      }

      // horizontal scrollbar
      if (target.offsetHeight > target.clientHeight) {
        if (pageY - document.documentElement.scrollTop > bottom - scrollBarSize) {
          return true;
        }
      }
    }
    return false;
  }

  selectWord(event, stores) {
    // before changing canvas operation let search manager know of touch event.
    const { xInPage, yInPage } = getTouchCoordinates(event, event.target);
    const pageIndex = getTouchPageIndex(event, stores);
    let searchManager = getSearchManager(stores);
    if (searchManager) searchManager.handleTouchPress();
    this.invokeTextManagerMethod("selectWord", undefined, xInPage, yInPage, event, pageIndex);
  }

  handleClickInFullScreeView(evtObj, pageIndex, stores) {
    this._performHitTesting(evtObj, pageIndex, stores)
      .then(contentType => {
        if (contentType !== hitTester.pageContentTypes.HYPERLINK) {
          if (evtObj.button === 0) {
            // left mouse button pressed
            stores.pagedViewState.goToNextPage();
          } else if (evtObj.button === 2) {
            // right mouse button pressed
            stores.pagedViewState.goToPrevPage();
          }
        }
      })
      .catch(() => {});
  }

  handleMouseDown(evtObj, pageIndex, stores) {
    const contentType = getLatestHitTestResponse(stores).contentType;
    if (isPanActive(stores)) {
      this._handlePanEvent(cursorType.GRABBING);
    }
    const { x, y } = getCoordinatesRelativeToPage(evtObj);
    this._mouseDownPos = { x, y };
    const mode = getCaretMode(stores) ? textManagerMode.CARET : textManagerMode.SELECTION;
    if (
      mode === textManagerMode.SELECTION ||
      (mode === textManagerMode.CARET && !shouldHideCaret(evtObj.target) && isCaretModeEnabledInActiveVerb(stores))
    ) {
      this.invokeTextManagerMethod("handleMouseDown", undefined, x, y, evtObj, pageIndex, true, mode, contentType);
    }
    if (!isThereAnySelection(stores)) contextMenuManager.handleMouseDown(stores);
    let searchManager = getSearchManager(stores);
    if (searchManager) {
      searchManager.handleMouseDown(evtObj, pageIndex);
    }
    if (
      findPressedMouseButton(evtObj) === mouseButtons.MIDDLE &&
      contentType === hitTester.pageContentTypes.HYPERLINK
    ) {
      evtObj.preventDefault();
    }
  }

  handleMouseDownCapture(evt, stores) {
    if (getCaretMode(stores) && !evt.shiftKey) {
      resetCaretMode();
    }
  }

  clearPageRects(stores, startPage, endPage, clearTouchSel = true) {
    if (clearTouchSel || isTouchSelectionPresent(stores) !== true)
      this.invokeTextManagerMethod("clearSelectionInPageRange", undefined, startPage, endPage);
  }

  handleTouchDrag(event, stores) {
    const { xInPage, yInPage } = getTouchCoordinates(event, event.target);
    const pageIndex = getTouchPageIndex(event, stores);
    this.invokeTextManagerMethod("handleTouchDrag", undefined, xInPage, yInPage, event, pageIndex);
  }

  handleMouseDownOnDocument(evt, stores) {
    if (this._isClickOnScrollBar(evt, stores)) return;
    this.invokeTextManagerMethod("clearSelection", undefined, stores);
    let searchManager = getSearchManager(stores);
    if (searchManager) {
      searchManager.handleMouseDownOnDocument();
    }
  }

  handleMouseUp(evtObj, pageIndex, stores) {
    if (isPanActive(stores)) {
      this._handlePanEvent(cursorType.GRAB);
    }
    if (this._dragInProgress) {
      this._dragInProgress = false;
      // Since we dont do hit testing when dragging, we need to do a one off hit testing here.
      this._performHitTesting(evtObj, pageIndex, stores);
    }
    this.invokeTextManagerMethod("handleMouseUp", undefined, evtObj);
    const { x, y } = getCoordinatesRelativeToPage(evtObj);
    if (this._mouseDownPos && Math.abs(x - this._mouseDownPos.x) <= 4 && Math.abs(y - this._mouseDownPos.y) <= 4) {
      this.invokeTextManagerMethod("handleClick", undefined, evtObj);
      if (
        findPressedMouseButton(evtObj) === mouseButtons.MIDDLE &&
        getLatestHitTestResponse(stores).contentType === hitTester.pageContentTypes.HYPERLINK
      ) {
        evtObj.preventDefault();
        this._hyperLinkManager.handleClick(evtObj, pageIndex, stores);
      }
    }
  }

  handleClick(evt, pageIndex, stores) {
    // verify if the click is happening at the same point that the mouseDown was started at.
    const { x, y } = getCoordinatesRelativeToPage(evt);
    if (this._mouseDownPos && Math.abs(x - this._mouseDownPos.x) <= 4 && Math.abs(y - this._mouseDownPos.y) <= 4) {
      this.invokeTextManagerMethod("handleClick", undefined, evt);
      this._performHitTesting(evt, pageIndex, stores).then(contentType => {
        if (contentType === hitTester.pageContentTypes.HYPERLINK) {
          return this._hyperLinkManager.handleClick(evt, pageIndex, stores);
        } else if (isFullScreenMode(stores) && !this._dragInProgress) {
          stores.pagedViewState.goToNextPage();
        }
      });
    }
    return Promise.resolve();
  }

  getTextManagerInstance() {
    return this._textManager;
  }

  onContextMenu(eventObj, stores, extraParams) {
    let promises = [];

    if (extraParams && extraParams.pageIndex >= 0) {
      const pageIndex = extraParams.pageIndex;
      const { x, y } = getCoordinatesRelativeToPage(eventObj);
      promises = [
        this.invokeTextManagerMethod("onContextMenu", Promise.resolve([]), { pageIndex, x, y }, stores),
        this._hyperLinkManager.onContextMenu({ pageIndex, x, y }, stores)
      ];
    }
    return Promise.all(promises)
      .then(response => flatten(response))
      .catch(() => []);
  }

  async onContextMenuRightClickOutline() {
    if (getShouldEnableOutline(stores) && !getTextInSelection(stores)) {
      const result = getCurrRHPFeature(stores) === OUTLINE;
      if (!result) {
        return Promise.resolve([OUTLINE]);
      }
    }
    return Promise.resolve([]);
  }

  onContextMenuTextSelectionRightClick() {
    if (getTextInSelection(stores)) {
      return Promise.resolve([VERB_COPY_TEXT]);
    }
    return Promise.resolve([]);
  }

  async onContextMenuRightClick() {
    if (getShouldEnableQnA(stores) && !getTextInSelection(stores)) {
      const result = getCurrRHPFeature(stores) === VERB_QANDA;
      if (!result) return Promise.resolve([VERB_QANDA]);
    }
    return Promise.resolve([]);
  }

  onContextMenuTextSelection() {
    if (getTextInSelection(stores)) {
      try {
        const returnVal = {
          component: <CopyTextIcon textManager={this._textManager} />,
          position: 40,
          width: 32,
          height: 32
        };
        return Promise.resolve([returnVal]);
      } catch (err) {
        console.log(err);
      }
    }
    return Promise.resolve([]);
  }

  handleScroll(event) {
    // if mouse state is down then we are in text selection mode, so extend selection.
    let { pageIndex, xInPage, yInPage } = getPageCoordinates(
      stores,
      this._textManager.lastMousePosition.x,
      this._textManager.lastMousePosition.y
    );
    this.invokeTextManagerMethod("handleScroll", undefined, xInPage, yInPage, pageIndex);
  }
}

export const pageContentHandler = new PageContentHandler();
